// Application detail page: shows everything the merchant submitted +
// inline document previews + approve/reject/request-info actions.

import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  approveApplication,
  getApplication,
  rejectApplication,
  requestMoreInfo,
} from '../../services/businessService';
import ConfirmDialog from '../common/ConfirmDialog';
import PortalModal, { ModalActions } from '../common/PortalModal';

const STATUS_BADGE = {
  pending: 'badge-pending',
  approved: 'badge-active',
  rejected: 'badge-inactive',
  more_info: 'badge-pending',
};
const STATUS_LABEL = {
  pending: 'Pending review',
  approved: 'Approved',
  rejected: 'Rejected',
  more_info: 'More info needed',
};

export default function AdminApplicationDetail() {
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showApprove, setShowApprove] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [notes, setNotes] = useState('');

  const load = () =>
    getApplication(id).then(setApp).catch(e => setError(e?.response?.data?.error || e.message));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (error) return <><div className="portal-content-header"><h1>Application</h1></div><div className="portal-error">{error}</div></>;
  if (!app) return <><div className="portal-content-header"><h1>Application</h1></div><div className="portal-empty">Loading…</div></>;

  const isDecided = app.status === 'approved' || app.status === 'rejected';

  const doApprove = async () => {
    setBusy(true);
    try {
      await approveApplication(id, notes);
      setShowApprove(false);
      setNotes('');
      load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(false); }
  };

  const doReject = async () => {
    if (!notes.trim()) {
      setError('A reason is required when rejecting an application.');
      return;
    }
    setBusy(true);
    try {
      await rejectApplication(id, notes);
      setShowReject(false);
      setNotes('');
      load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(false); }
  };

  const doMoreInfo = async () => {
    if (!notes.trim()) {
      setError('Please describe what additional information is needed.');
      return;
    }
    setBusy(true);
    try {
      await requestMoreInfo(id, notes);
      setShowMoreInfo(false);
      setNotes('');
      load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="portal-content-header">
        <h1>
          {app.company_name}
          <span className={`badge ${STATUS_BADGE[app.status] || 'badge-pending'}`}>{STATUS_LABEL[app.status] || app.status}</span>
        </h1>
        <Link to="/admin/applications" className="portal-button secondary">← Back</Link>
      </div>

      <div className="portal-card">
        <div className="portal-defs">
          <Def label="Company name" value={app.company_name} />
          <Def label="Business type" value={app.business_type} />
          <Def label="Contact person" value={app.contact_name} />
          <Def label="Contact phone" value={app.contact_phone || '—'} />
          <Def label="Email" value={app.user?.email || '—'} />
          <Def label="WhatsApp" value={app.store?.whatsapp_number || '—'} />
          <Def label="Website" value={app.website ? <a href={app.website} target="_blank" rel="noreferrer" style={{ color: 'var(--tudu-accent-lavender)' }}>{app.website}</a> : '—'} />
          <Def label="Submitted" value={new Date(app.created_at).toLocaleString()} />
          <Def label="Reviewed by" value={app.reviewed_by || '—'} />
          <Def label="Reviewed at" value={app.reviewed_at ? new Date(app.reviewed_at).toLocaleString() : '—'} />
        </div>
        {app.address && (
          <div style={{ marginTop: 'var(--tudu-space-4)' }}>
            <div className="portal-def-label">Address</div>
            <div className="portal-def-value" style={{ whiteSpace: 'pre-wrap' }}>{app.address}</div>
          </div>
        )}
        {app.description && (
          <div style={{ marginTop: 'var(--tudu-space-4)' }}>
            <div className="portal-def-label">Description</div>
            <div className="portal-def-value" style={{ whiteSpace: 'pre-wrap', fontWeight: 400 }}>{app.description}</div>
          </div>
        )}
      </div>

      {app.admin_notes && (
        <>
          <h3 className="portal-section-label">Admin notes</h3>
          <div className="portal-card" style={{ whiteSpace: 'pre-wrap', color: 'var(--tudu-text)' }}>{app.admin_notes}</div>
        </>
      )}

      <h3 className="portal-section-label">Documents ({app.documents.length})</h3>
      {app.documents.length === 0 ? (
        <div className="portal-empty">No documents attached.</div>
      ) : (
        <div className="portal-card" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 'var(--tudu-space-4)' }}>
          {app.documents.map(doc => (
            <DocCard key={doc.id} doc={doc} />
          ))}
        </div>
      )}

      {!isDecided && (
        <>
          <h3 className="portal-section-label">Decision</h3>
          <div className="portal-card" style={{ display: 'flex', gap: 'var(--tudu-space-3)', flexWrap: 'wrap' }}>
            <button type="button" className="portal-button" onClick={() => { setNotes(''); setShowApprove(true); setError(''); }}>Approve</button>
            <button type="button" className="portal-button secondary" onClick={() => { setNotes(''); setShowMoreInfo(true); setError(''); }}>Request more info</button>
            <button type="button" className="portal-button danger" onClick={() => { setNotes(''); setShowReject(true); setError(''); }}>Reject</button>
          </div>
        </>
      )}

      {isDecided && (
        <div className="portal-card info" style={{ marginTop: 'var(--tudu-space-4)' }}>
          This application has been {app.status}. Use the user management page if you need to
          change the merchant's role or deactivate their account.
        </div>
      )}

      <ConfirmDialog
        open={showApprove}
        title="Approve this application?"
        message={`Approving "${app.company_name}" will activate their store, switch them to the 'merchant' role, and create a default Free-tier subscription. They can start uploading flyers immediately.`}
        confirmLabel="Approve"
        onConfirm={doApprove}
        onCancel={() => setShowApprove(false)}
      />

      <PortalModal
        open={showReject}
        onClose={() => setShowReject(false)}
        title="Reject application"
        size="narrow"
      >
        <p>
          The merchant will see this note on their pending screen. Be specific so they
          know whether to fix and resubmit or move on.
        </p>
        {error && <div className="portal-error">{error}</div>}
        <textarea
          className="portal-textarea"
          style={{ width: '100%', minHeight: 120 }}
          placeholder="Reason for rejection (required)"
          value={notes}
          onChange={e => setNotes(e.target.value)}
        />
        <ModalActions>
          <button type="button" className="portal-button secondary" onClick={() => setShowReject(false)}>Cancel</button>
          <button type="button" className="portal-button danger" disabled={busy} onClick={doReject}>
            {busy ? 'Rejecting…' : 'Reject application'}
          </button>
        </ModalActions>
      </PortalModal>

      <PortalModal
        open={showMoreInfo}
        onClose={() => setShowMoreInfo(false)}
        title="Request more information"
        size="narrow"
      >
        <p>
          The application stays open. The merchant sees this note on their pending screen
          and can email back with the requested information.
        </p>
        {error && <div className="portal-error">{error}</div>}
        <textarea
          className="portal-textarea"
          style={{ width: '100%', minHeight: 120 }}
          placeholder="What do you need from them?"
          value={notes}
          onChange={e => setNotes(e.target.value)}
        />
        <ModalActions>
          <button type="button" className="portal-button secondary" onClick={() => setShowMoreInfo(false)}>Cancel</button>
          <button type="button" className="portal-button" disabled={busy} onClick={doMoreInfo}>
            {busy ? 'Sending…' : 'Send request'}
          </button>
        </ModalActions>
      </PortalModal>
    </>
  );
}

function Def({ label, value }) {
  return (
    <div>
      <div className="portal-def-label">{label}</div>
      <div className="portal-def-value">{value}</div>
    </div>
  );
}

// Document preview tile — inline preview for images, download link for PDFs/others.
function DocCard({ doc }) {
  const isImage = doc.content_type?.startsWith('image/');
  const isPdf = doc.content_type === 'application/pdf';
  return (
    <div style={{ background: 'var(--tudu-bg-3)', border: '1px solid var(--tudu-line-2)', borderRadius: 'var(--tudu-radius-sm)', padding: 'var(--tudu-space-3)' }}>
      <div className="portal-def-label">{doc.kind.replace('_', ' ')}</div>
      <div style={{ marginTop: 'var(--tudu-space-2)' }}>
        {isImage ? (
          <a href={doc.data_uri} target="_blank" rel="noreferrer">
            <img
              src={doc.data_uri}
              alt={doc.filename}
              style={{ width: '100%', borderRadius: 'var(--tudu-radius-xs)', display: 'block', maxHeight: 160, objectFit: 'cover' }}
            />
          </a>
        ) : isPdf ? (
          <a href={doc.data_uri} target="_blank" rel="noreferrer" className="portal-button secondary" style={{ display: 'inline-block', textDecoration: 'none' }}>
            Open PDF
          </a>
        ) : (
          <a href={doc.data_uri} download={doc.filename} className="portal-button secondary" style={{ display: 'inline-block', textDecoration: 'none' }}>
            Download
          </a>
        )}
      </div>
      <div style={{ marginTop: 'var(--tudu-space-2)', fontSize: 'var(--tudu-fs-xs)', color: 'var(--tudu-text-lo)' }}>
        {doc.filename || 'unnamed'} · {new Date(doc.uploaded_at).toLocaleDateString()}
      </div>
    </div>
  );
}
